import React, { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import InfrastructureSummary from './InfrastructureSummary';
import InfrastructureSettingsPopover from './InfrastructureSettingsPopover';
import ConditionalRender from '../../../shared/components/ConditionalRender';
import useHomeService from '../../../services/HomeService/home.service';
import { SUMMARY_SERVICE_IDS } from '../YourServices/constants';
import { DashboardService, DashboardSettings, InfrastructureSettings, MarsSettings } from '../../../models/dashboard.model';
import { QueryKeys } from '../../../enums/queryKeys';
import styles from './serviceSummary.module.scss';

const MARS_SETTINGS: MarsSettings = { claimSummary: false };

interface ServiceSummaryProps {
  dashboardServices: DashboardService[];
}

const ServiceSummary: React.FC<ServiceSummaryProps> = ({ dashboardServices }) => {
  const { getDashboardSettings, updateDashboardSettings } = useHomeService();
  const queryClient = useQueryClient();
  // Empty while the services load or if they fail, so a user without the service never triggers its calls.
  const hasSummaryService = dashboardServices.some(({ id }) => SUMMARY_SERVICE_IDS.has(id));
  const {
    data: settings,
    isPending: loadingSettings,
    isLoadingError: settingsLoadFailed,
  } = useQuery({ ...getDashboardSettings(), enabled: hasSummaryService });
  const { mutate: saveSettings } = useMutation(updateDashboardSettings());

  // Pending toggles, kept out of the query cache. null means the popover is
  // closed, so discarding the draft is what reverts an unconfirmed change.
  const [draft, setDraft] = useState<InfrastructureSettings | null>(null);

  const savedWorkOrderSummary = settings?.infrastructure?.workOrderSummary ?? true;
  const savedWorkOrderCalendar = settings?.infrastructure?.workOrderCalendar ?? true;

  // The widgets follow the draft too, so toggles preview immediately.
  const workOrderSummaryVisible = draft?.workOrderSummary ?? savedWorkOrderSummary;
  const workOrderCalendarVisible = draft?.workOrderCalendar ?? savedWorkOrderCalendar;

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setDraft(open ? { workOrderSummary: savedWorkOrderSummary, workOrderCalendar: savedWorkOrderCalendar } : null);
    },
    [savedWorkOrderSummary, savedWorkOrderCalendar],
  );

  const handleWorkOrderSummaryChange = useCallback((checked: boolean) => {
    setDraft((prev) => (prev ? { ...prev, workOrderSummary: checked } : prev));
  }, []);

  const handleWorkOrderCalendarChange = useCallback((checked: boolean) => {
    setDraft((prev) => (prev ? { ...prev, workOrderCalendar: checked } : prev));
  }, []);

  const handleOk = useCallback(() => {
    if (!draft) return;

    const changed = draft.workOrderSummary !== savedWorkOrderSummary || draft.workOrderCalendar !== savedWorkOrderCalendar;

    if (!changed) {
      setDraft(null);
      return;
    }

    const payload: DashboardSettings = { infrastructure: draft, mars: MARS_SETTINGS };

    // Promote the preview to saved state before dropping the draft, otherwise
    // the widgets snap back to the old values until the refetch lands.
    queryClient.setQueryData<DashboardSettings>([QueryKeys.GET_DASHBOARD_SETTINGS], payload);
    setDraft(null);

    saveSettings(payload, {
      // Settled, not success: on a failed save this refetch is what restores the
      // real server state instead of leaving the optimistic value on screen.
      onSettled: () => queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_DASHBOARD_SETTINGS] }),
    });
  }, [draft, savedWorkOrderSummary, savedWorkOrderCalendar, saveSettings, queryClient]);

  // Only the initial load failing hides the section; a failed background refetch
  // still has valid cached data to show.
  if (!hasSummaryService || settingsLoadFailed) return null;

  return (
    <section className={styles.section} aria-labelledby="home-service-summary">
      <div className={styles.headingRow}>
        <h2 className={styles.heading} id="home-service-summary">
          Service Summary
        </h2>
        <InfrastructureSettingsPopover
          open={draft !== null}
          onOpenChange={handleOpenChange}
          workOrderSummaryVisible={workOrderSummaryVisible}
          onWorkOrderSummaryVisibleChange={handleWorkOrderSummaryChange}
          workOrderCalendarVisible={workOrderCalendarVisible}
          onWorkOrderCalendarVisibleChange={handleWorkOrderCalendarChange}
          onOk={handleOk}
        >
          {/* Disabled until settings load, so OK can never save guessed defaults
              over what this user actually has stored. */}
          <button type="button" className={styles.settingsButton} disabled={loadingSettings}>
            <img className={styles.settings} src={'/assets/settings-icon.svg'} alt="Infrastructure Manager settings" />
          </button>
        </InfrastructureSettingsPopover>
      </div>

      <ConditionalRender visible={!loadingSettings && (workOrderSummaryVisible || workOrderCalendarVisible)}>
        <InfrastructureSummary showWorkOrderSummary={workOrderSummaryVisible} showWorkOrderCalendar={workOrderCalendarVisible} />
      </ConditionalRender>
    </section>
  );
};

export default ServiceSummary;
